import { useCallback, useEffect, useRef } from "react"

interface UseInfiniteScrollOptions {
	hasMore: boolean
	status: string
	loadMore: (numItems: number) => void
	/** Number of items to load per page (default: 20) */
	numItems?: number
	/** IntersectionObserver root margin (default: "300px") */
	rootMargin?: string
}

export function useInfiniteScroll({
	hasMore,
	status,
	loadMore,
	numItems = 20,
	rootMargin = "300px",
}: UseInfiniteScrollOptions) {
	const loadMoreRef = useRef<HTMLDivElement>(null)

	const handleIntersection = useCallback(
		(entries: IntersectionObserverEntry[]) => {
			const [entry] = entries
			if (entry?.isIntersecting && hasMore && status === "CanLoadMore") {
				loadMore(numItems)
			}
		},
		[hasMore, status, loadMore, numItems],
	)

	useEffect(() => {
		const node = loadMoreRef.current
		if (!node) {
			return
		}

		const observer = new IntersectionObserver(handleIntersection, {
			rootMargin,
		})
		observer.observe(node)

		return () => observer.disconnect()
	}, [handleIntersection, rootMargin])

	return loadMoreRef
}
